import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useLayoutEffect
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { get } from 'lodash';
import SearchComponent from './SearchMC';
import IconMC from './IconMC';
import TooltipMC from './TooltipMC';

/**
 * DropdownMC component
 *
 * @param {string} id - **Required** - HTML 'id' attribute for the select element (used for accessibility and label association).
 * @param {string} label - **Required** - Text label to display above the dropdown.
 * @param {Function} onSelect - **Optional** - Function to be called when an option is selected. It receives the selected value (`String | Number`) as an argument.
 * @param {string | number} [defaultValue] - **Optional** - The initial value to be pre-selected in the dropdown. Should match one of the `id` values in the `options` array.
 * @param {boolean} [search] - **Optional** - If `true`, a search input will be displayed above the dropdown list to filter the options.
 * @param {string} [name] - **Optional** - The name of the field to be used in the form. If not provided, the `id` will be used.
 * @param {boolean} [useFloatingLabel] - **Optional** - Whether to use floating label style.
 * @param {Array<IOptionProps>} options - **Required** - Array of option objects to populate the dropdown.
 * @param {string} [className] - **Optional** - Additional classes to apply to the dropdown.
 * @param {string} [placeholder] - **Optional** - Placeholder text to display when no option is selected.
 * @param {string} [labelClassName] - **Optional** - Additional classes to apply to the displayed value label.
 * @param {Function} [filterOption] - **Optional** - Function to filter the options. It receives the search term and the option object as arguments.
 * @param {Function} [renderOption] - **Optional** - Function to custom render each option in the dropdown list. It receives the option object as an argument and should return JSX.
 * @param {Function} [renderSelected] - **Optional** - Function to custom render the selected value display. It receives the selected option object as an argument and should return JSX.
 *
 * @typedef {Object} IOptionProps
 * @property {string | number} id - **Required** - Unique identifier for the option (used as the value in the dropdown).
 * @property {string} label - **Required** - Text to display in the dropdown list for this option.
 * @property {React.ReactNode} [icon] - **Optional** - Icon to display next to the option.
 * @property {string} [color] - **Optional** - Color to display next to the option. Must be in the format #AAAAAA.
 */

const FloatingLabelTopPx = 5;
const FloatingLabelGapBelowPx = 4;
const FloatingLabelSingleLinePaddingTopPx = 20;
const FloatingLabelMultiLineHeightThresholdPx = 28;

const DropdownMC = ({
  id,
  label,
  onSelect,
  defaultValue,
  search,
  name,
  placeholder,
  labelClassName,
  filterOption,
  renderOption,
  renderSelected,
  options = [],
  useFloatingLabel = false,
  className = '',
  rowDropdown = false,
  disabled = false,
  buttonClassName: buttonClassNameProp = '',
  rules = {},
  labelTooltip,
  onCreateOption
}) => {
  const { t } = useTranslation();
  const floatingLabelRef = useRef(null);
  const [floatingLabelHeightPx, setFloatingLabelHeightPx] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [selectedValue, setSelectedValue] = useState(defaultValue);
  const [panelPosition, setPanelPosition] = useState(null);
  const controlForm = useFormContext();

  const wrapperRef = useRef(null);
  const panelRef = useRef(null);
  const buttonRef = useRef(null);

  const updatePanelPosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const panelWidth = search ? Math.max(260, rect.width) : rect.width;
    // Centrar el panel bajo el trigger; con search el panel es más ancho que el botón y alinear por la derecha lo empujaba hacia la izquierda.
    const rawLeft = rect.left + (rect.width - panelWidth) / 2;
    const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 0;
    const left = search
      ? Math.max(8, Math.min(rawLeft, Math.max(8, vw - panelWidth - 8)))
      : rect.left;

    const estimatedHeight = search ? 310 : 260; // search bar + max-height 240px + padding
    const positionStyles = { left, width: panelWidth };

    if (rect.bottom + estimatedHeight > vh && rect.top > estimatedHeight) {
      positionStyles.bottom = vh - rect.top + 4;
    } else {
      positionStyles.top = rect.bottom + 4;
    }

    setPanelPosition(positionStyles);
  }, [search]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPanelPosition(null);
      return;
    }
    updatePanelPosition();
  }, [isOpen, updatePanelPosition]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleMouseDown = (event) => {
      const insideWrapper = wrapperRef.current?.contains(event.target);
      const insidePanel = panelRef.current?.contains(event.target);
      if (insideWrapper || insidePanel) return;
      if (!disabled) setIsOpen(false);
    };
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [isOpen, disabled, updatePanelPosition]);

  useLayoutEffect(() => {
    if (!useFloatingLabel) {
      setFloatingLabelHeightPx(null);
      return undefined;
    }

    const el = floatingLabelRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const updateHeight = () => {
      setFloatingLabelHeightPx(Math.ceil(el.getBoundingClientRect().height));
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, [useFloatingLabel, label]);

  useEffect(() => {
    if (defaultValue === undefined || defaultValue === null) return;
    const selectedOption = options.find((option) => String(option.id) === String(defaultValue));
    if (selectedOption) {
      setSelectedValue(selectedOption.id);
    }
  }, [defaultValue, options]);

  useEffect(() => {
    // Si el dropdown está cerrado, restablecer el filtro
    if (!isOpen) {
      setSearchTerm('');
      setFilteredOptions(options);
      return;
    }
    // Si está abierto, filtrar normalmente
    const filtered = filterOption ? filterOption(searchTerm) : options.filter((option) => option?.label?.toLowerCase().includes(searchTerm.toLowerCase()));
    setFilteredOptions(filtered);
  }, [searchTerm, options, isOpen]);

  const handleSelect = (optionId, field) => {
    if (disabled) return;

    setSelectedValue(optionId);
    setIsOpen(false);
    field?.field?.onChange(optionId);
    if (onSelect) {
      onSelect(optionId);
    }
    if (controlForm?.formState?.errors[name]) {
      controlForm?.trigger(name);
    }
  };

  const selectedOption = options.find((option) => String(option.id) === String(selectedValue));
  const displayedValue = selectedOption?.selectedLabel || selectedOption?.label || placeholder || t('common.all');
  const dotStyle = (color) => ({
    backgroundColor: color
  });

  const defaultRenderOption = (option, isSelected = false) => (
    <>
      {option.icon && <IconMC name={option.icon} size="sm" className="tw-mr-2" />}
      {option.image && <img src={option.image} alt="" className="tw-w-4 tw-h-4 tw-mr-2 tw-object-contain" />}
      {option.color && (
        <div
          className="tw-w-2 tw-h-2 tw-block tw-rounded-full tw-mr-2"
          style={dotStyle(option.color)}
        />
      )}
      <span className="tw-overflow-hidden tw-block tw-text-ellipsis tw-w-4/5">
        {option.label}
      </span>
    </>
  );

  const defaultRenderSelected = (option) => (
    <>
      {option?.icon && <IconMC name={option.icon} size="sm" className="tw-mr-2 tw-text-gray-500" />}
      {option?.image && <img src={option.image} alt="" className="tw-w-4 tw-h-4 tw-mr-2 tw-object-contain" />}
      {option?.color && (
        <div
          className="tw-w-2 tw-h-2 tw-block tw-rounded-full tw-mr-2"
          style={dotStyle(option.color)}
        />
      )}
      <span className={`tw-block tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap ${maxWidthClass} ${labelClassName || ''}`}>
        {option?.label || displayedValue}
      </span>
    </>
  );

  const buttonClassName = [
    'tw-bg-gray-50',
    'tw-text-default',
    'tw-rounded-xl',
    'tw-w-full',
    'tw-px-4',
    'tw-flex',
    useFloatingLabel ? 'tw-items-start' : 'tw-items-center',
    'tw-justify-between',
    'tw-border',
    'tw-relative',
    (() => {
      if (useFloatingLabel) return '';
      if (rowDropdown) return '';
      return 'tw-py-3';
    })(),
    buttonClassNameProp
  ].join(' ');

  const portalPanelClassName = [
    'tw-bg-white',
    'tw-border',
    useFloatingLabel ? 'tw-rounded-xl' : 'tw-rounded-md',
    'tw-shadow-lg',
    'tw-overflow-hidden'
  ].join(' ');

  const listItemClassName = [
    'tw-px-4',
    'tw-py-2',
    'hover:tw-bg-gray-100',
    'tw-flex',
    'tw-items-center',
    'tw-cursor-pointer',
    'tw-break-words'
  ].join(' ');

  const listItemSelectedClassName = 'tw-bg-blue-50 tw-text-blue-700';

  const floatingLabelClassNames = [
    'tw-absolute',
    'tw-block',
    'tw-text-start',
    'tw-top-[5px]',
    'tw-z-10',
    'tw-leading-snug',
    'tw-start-4',
    'tw-end-12',
    'tw-flex',
    'tw-items-center',
    'tw-gap-1'
  ].join(' ');

  let floatingButtonPaddingTopPx = null;
  if (useFloatingLabel) {
    const isMultiLineLabel = floatingLabelHeightPx != null
      && floatingLabelHeightPx > FloatingLabelMultiLineHeightThresholdPx;
    floatingButtonPaddingTopPx = isMultiLineLabel
      ? FloatingLabelTopPx + floatingLabelHeightPx + FloatingLabelGapBelowPx
      : FloatingLabelSingleLinePaddingTopPx;
  }

  const iconTopClass = rowDropdown ? 'tw-top-[18%]' : 'tw-top-1/2 tw--translate-y-1/2';
  const maxWidthClass = rowDropdown ? 'tw-max-w-[160px]' : '';

  const renderDropdownPanel = (field) => {
    const fixedStyle = panelPosition
      ? {
        position: 'fixed',
        top: panelPosition.top,
        bottom: panelPosition.bottom,
        left: panelPosition.left,
        width: panelPosition.width,
        zIndex: 10006
      }
      : { display: 'none' };

    return (
      <div className="tw-wrapper" style={fixedStyle}>
        <div
          ref={panelRef}
          className={portalPanelClassName}
        >
          {search && (
            <div className="tw-px-2 tw-pt-2 tw-pb-1">
              <SearchComponent
                placeholder={t('dropdown_mc.search')}
                onChange={setSearchTerm}
                inputClassName="tw-text-base tw-bg-white !tw-py-3 !tw-min-h-[48px]"
                debounceTime={200}
              />
            </div>
          )}
          <ul className="tw-list-none tw-m-0 tw-py-1 tw-overflow-y-auto tw-max-h-60 tw-overflow-x-hidden tw-pl-0">
            {filteredOptions.length === 0 ? (
              searchTerm.trim() && onCreateOption ? (
                <li
                  onClick={() => {
                    onCreateOption(searchTerm);
                    setIsOpen(false);
                  }}
                  className="tw-px-4 tw-py-2 tw-text-blue-500 hover:tw-bg-gray-100 tw-cursor-pointer tw-font-semibold"
                >
                  {t('dropdown_mc.create_option', { defaultValue: 'Crear "{{term}}"' }).replace('{{term}}', searchTerm)}
                </li>
              ) : (
                <li className="tw-px-4 tw-py-2 tw-text-gray-500">
                  {t('dropdown_mc.no_options')}
                </li>
              )
            ) : (
              filteredOptions.map((option) => (
                <li
                  key={option.id}
                  onClick={() => handleSelect(option.id, field)}
                  className={`${listItemClassName} ${String(selectedValue) === String(option.id) ? listItemSelectedClassName : ''}`}
                >
                  {renderOption ? renderOption(option, selectedValue === option.id) : defaultRenderOption(option, selectedValue === option.id)}
                </li>
              ))
            )}
          </ul>
        </div>
      </div>
    );
  };

  const dropdownContent = (field) => (
    <div className={`tw-wrapper tw-relative tw-w-full ${className}`} ref={wrapperRef}>
      {!useFloatingLabel && label && (
        <label
          htmlFor={id}
          className="tw-text-zinc-700 tw-mb-1 tw-text-sm tw-font-bold"
        >
          {label}
        </label>
      )}
      <button
        ref={buttonRef}

        type="button"

        disabled={disabled}

        onClick={() => !disabled && setIsOpen(!isOpen)}

        className={buttonClassName}

        style={floatingButtonPaddingTopPx != null ? { paddingTop: `${floatingButtonPaddingTopPx}px` } : undefined}
      >
        {useFloatingLabel && (
          <div ref={floatingLabelRef} className={floatingLabelClassNames}>
            <label htmlFor={id} className="tw-mb-0 tw-text-xs tw-text-blue-500 tw-truncate tw-pointer-events-none">
              {label}
            </label>
            {labelTooltip && (
              <TooltipMC text={labelTooltip} position="right">
                <IconMC name="info-outline" size="xs" className="tw-text-blue-500 tw-flex-shrink-0" />
              </TooltipMC>
            )}
          </div>
        )}
        <div className={`tw-w-full tw-pr-2 ${useFloatingLabel ? 'tw-pb-2' : ''}`}>
          <span className={`tw-flex tw-items-center tw-overflow-hidden tw-w-full ${useFloatingLabel ? 'tw-min-h-[22px]' : ''}`}>
            {renderSelected ? renderSelected(selectedOption) : defaultRenderSelected(selectedOption)}
          </span>
          <div className={`tw-absolute ${iconTopClass} tw-right-4`}>
            <IconMC
              name={`arrow-ios-${isOpen ? 'up' : 'down'}ward-outline`}
              size="base"
              className="tw-text-gray-600"
            />
          </div>
        </div>
      </button>
      {get(controlForm?.formState?.errors, name) && <p className="tw-text-xs tw-text-red-500">{get(controlForm?.formState?.errors, name).message}</p>}
      {isOpen && panelPosition && typeof document !== 'undefined' && createPortal(renderDropdownPanel(field), document.body)}
    </div>
  );

  if (!controlForm?.control) {
    return dropdownContent();
  }

  return (
    <Controller
      name={name || id}
      control={controlForm.control}
      defaultValue={defaultValue || ''}
      rules={rules}
      render={dropdownContent}
    />
  );
};

export default DropdownMC;
